import type { PDFDocument, PDFFont, PDFPage, RGB } from "pdf-lib";

/**
 * Prescription pad renderer — the printable form of a print-only signed
 * prescription.
 *
 * Letter page, standard Helvetica faces only (nothing embedded except the
 * optional provider signature image), so the pad renders identically in every
 * viewer and printer. pdf-lib loads lazily: signing is a rare, explicit action
 * and the library must stay out of the encounter page's initial bundle.
 *
 * One medication prints the single-medication pad. Several (RX-3) print as a
 * numbered list on one prescription: a page that fills continues on the next,
 * which repeats the letterhead and patient band and says "Page N of M", and
 * the prescriber signs once, after the last medication.
 *
 * The bytes are reproducible for a given content + signature image: document
 * dates come from the signing timestamp, never the wall clock.
 */

export interface PrescriptionPadClinic {
  readonly name: string;
  readonly address?: string | undefined;
  readonly phone?: string | undefined;
  readonly fax?: string | undefined;
}

export interface PrescriptionPadPatientIdentifier {
  /** "Health Card #" for a provincial/federal plan, "Member ID" otherwise. */
  readonly label: string;
  readonly value: string;
  /** Payer name, printed small under the value. */
  readonly note?: string | undefined;
}

export interface PrescriptionPadMedication {
  readonly medication: string;
  readonly sig: string;
  readonly dispense: string;
  readonly refills: string;
  /** Indication, substitution, days supply — one line each. */
  readonly details: readonly string[];
}

export interface PrescriptionPadContent {
  readonly clinic: PrescriptionPadClinic;
  readonly prescriber: {
    readonly name: string;
    readonly licenseLine: string;
  };
  readonly patient: {
    readonly name: string;
    readonly dateOfBirth: string;
    readonly mrn?: string | undefined;
    readonly address?: string | undefined;
    readonly identifier?: PrescriptionPadPatientIdentifier | undefined;
  };
  readonly date: string;
  /** In prescribing order; a single medication prints the one-medication pad. */
  readonly medications: readonly PrescriptionPadMedication[];
  readonly signedAt: string;
  /**
   * Drug-content attribution printed small above the footer (FDB disclaimer
   * and copyright, Health Canada DPD acknowledgement) when the medication came
   * from FDB MedKnowledge.
   */
  readonly sourceNotice?: readonly string[] | undefined;
}

export interface PrescriptionSignatureImage {
  readonly bytes: Uint8Array;
  readonly mimeType: "image/png" | "image/jpeg";
}

export interface RenderPrescriptionPadOptions {
  /** Signing timestamp; becomes the PDF creation and modification date. */
  readonly signedAtIso: string;
  readonly signatureImage?: PrescriptionSignatureImage | undefined;
}

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 48;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_HEIGHT = 40;
/** The signature block never drops below this baseline, whatever the body length. */
const SIGNATURE_LINE_MIN_Y = 150;
/** Gap between the last body line and the top of the signature image. */
const SIGNATURE_BLOCK_GAP = 110;
const SIGNATURE_IMAGE_MAX_WIDTH = 190;
const SIGNATURE_IMAGE_MAX_HEIGHT = 56;

/** WinAnsi is the only encoding the standard fonts carry; anything else prints as "?". */
export function pdfSafe(value: string): string {
  return value.replaceAll(/[\r\t]+/g, " ").replaceAll(/[^\n\x20-\x7e\xa0-\xff–—‘’“”•…]/g, "?");
}

/** Splits a single token wider than the column into fragments that each fit. */
function fittedFragments(font: PDFFont, word: string, size: number, width: number): string[] {
  if (font.widthOfTextAtSize(word, size) <= width) return [word];
  const fragments: string[] = [];
  let current = "";
  for (const character of word) {
    const candidate = `${current}${character}`;
    if (current !== "" && font.widthOfTextAtSize(candidate, size) > width) {
      fragments.push(current);
      current = character;
    } else {
      current = candidate;
    }
  }
  if (current !== "") fragments.push(current);
  return fragments;
}

/**
 * Greedy word wrap; an unbreakable token (a long identifier, say) is split
 * by character so nothing ever runs past its column.
 */
function wrappedLines(font: PDFFont, value: string, size: number, width: number): string[] {
  const output: string[] = [];
  for (const paragraph of pdfSafe(value).split("\n")) {
    const words = paragraph
      .split(/\s+/)
      .filter(Boolean)
      .flatMap((word) => fittedFragments(font, word, size, width));
    if (words.length === 0) {
      if (output.length > 0) output.push("");
      continue;
    }
    let line = words[0] as string;
    for (const word of words.slice(1)) {
      const candidate = `${line} ${word}`;
      if (font.widthOfTextAtSize(candidate, size) <= width) line = candidate;
      else {
        output.push(line);
        line = word;
      }
    }
    output.push(line);
  }
  return output;
}

interface Palette {
  readonly blue: RGB;
  readonly ink: RGB;
  readonly muted: RGB;
  readonly band: RGB;
  readonly rule: RGB;
}

interface Pen {
  readonly page: PDFPage;
  readonly regular: PDFFont;
  readonly bold: PDFFont;
  readonly palette: Palette;
}

function drawText(
  pen: Pen,
  text: string,
  options: {
    readonly x: number;
    readonly y: number;
    readonly size: number;
    readonly bold?: boolean;
    readonly color?: RGB;
    readonly align?: "left" | "right";
  },
): void {
  const font = options.bold ? pen.bold : pen.regular;
  const safe = pdfSafe(text);
  const x =
    options.align === "right" ? options.x - font.widthOfTextAtSize(safe, options.size) : options.x;
  pen.page.drawText(safe, {
    x,
    y: options.y,
    size: options.size,
    font,
    color: options.color ?? pen.palette.ink,
  });
}

function drawRule(pen: Pen, y: number, color: RGB, thickness = 0.75): void {
  pen.page.drawLine({
    start: { x: MARGIN, y },
    end: { x: PAGE_WIDTH - MARGIN, y },
    thickness,
    color,
  });
}

/** Draws a column of lines, returning the baseline y for the next line. */
function drawLines(
  pen: Pen,
  lines: readonly string[],
  options: {
    readonly x: number;
    readonly y: number;
    readonly size: number;
    readonly leading: number;
    readonly bold?: boolean;
    readonly color?: RGB;
    readonly align?: "left" | "right";
  },
): number {
  let y = options.y;
  for (const line of lines) {
    drawText(pen, line, { ...options, y });
    y -= options.leading;
  }
  return y;
}

/** Letterhead and patient band; returns the baseline where the body starts. */
function drawLetterheadAndPatient(pen: Pen, content: PrescriptionPadContent): number {
  const { palette, regular, bold } = pen;
  const right = PAGE_WIDTH - MARGIN;

  // ------------------------------------------------------------ letterhead
  const columnWidth = 300;
  let leftY = PAGE_HEIGHT - MARGIN - 18;
  drawText(pen, content.clinic.name, {
    x: MARGIN,
    y: leftY,
    size: 19,
    bold: true,
    color: palette.blue,
  });
  leftY -= 17;
  if (content.clinic.address !== undefined && content.clinic.address.trim() !== "") {
    leftY = drawLines(pen, wrappedLines(regular, content.clinic.address, 9, columnWidth), {
      x: MARGIN,
      y: leftY,
      size: 9,
      leading: 12,
    });
  }
  if (content.clinic.phone !== undefined && content.clinic.phone.trim() !== "") {
    drawText(pen, `Tel ${content.clinic.phone}`, { x: MARGIN, y: leftY, size: 9 });
    leftY -= 12;
  }
  if (content.clinic.fax !== undefined && content.clinic.fax.trim() !== "") {
    drawText(pen, `Fax ${content.clinic.fax}`, { x: MARGIN, y: leftY, size: 9 });
    leftY -= 12;
  }

  let rightY = PAGE_HEIGHT - MARGIN - 14;
  drawText(pen, content.prescriber.name, {
    x: right,
    y: rightY,
    size: 11,
    bold: true,
    align: "right",
  });
  rightY -= 13;
  rightY = drawLines(pen, wrappedLines(regular, content.prescriber.licenseLine, 9, 220), {
    x: right,
    y: rightY,
    size: 9,
    leading: 12,
    color: palette.muted,
    align: "right",
  });

  let y = Math.min(leftY, rightY) - 6;
  drawRule(pen, y, palette.blue, 1.5);
  y -= 14;

  // ---------------------------------------------------------- patient band
  const bandTop = y;
  const bandPadding = 12;
  const bandLeftX = MARGIN + bandPadding;
  const bandRightX = MARGIN + CONTENT_WIDTH / 2 + 12;
  // Every value in the band wraps to its column, so a long name, address,
  // identifier, or payer name grows the band instead of running off the page.
  const bandColumnWidth = CONTENT_WIDTH / 2 - bandPadding * 2;
  const patientNameLines = wrappedLines(bold, content.patient.name, 12, bandColumnWidth);
  const patientAddressLines =
    content.patient.address === undefined || content.patient.address.trim() === ""
      ? []
      : wrappedLines(regular, content.patient.address, 9, bandColumnWidth);
  const identifier = content.patient.identifier;
  const identifierValueLines =
    identifier === undefined ? [] : wrappedLines(regular, identifier.value, 10, bandColumnWidth);
  const identifierNoteLines =
    identifier?.note === undefined
      ? []
      : wrappedLines(regular, identifier.note, 7.5, bandColumnWidth);
  const leftBandHeight =
    patientNameLines.length * 14 +
    patientAddressLines.length * 12 +
    (content.patient.mrn === undefined ? 0 : 12);
  const rightBandHeight =
    26 +
    (identifier === undefined
      ? 0
      : 15 + identifierValueLines.length * 12 + identifierNoteLines.length * 10);
  const bandHeight = bandPadding * 2 + Math.max(leftBandHeight, rightBandHeight) + 4;
  pen.page.drawRectangle({
    x: MARGIN,
    y: bandTop - bandHeight,
    width: CONTENT_WIDTH,
    height: bandHeight,
    color: palette.band,
  });

  let bandY = bandTop - bandPadding - 10;
  bandY = drawLines(pen, patientNameLines, {
    x: bandLeftX,
    y: bandY,
    size: 12,
    leading: 14,
    bold: true,
  });
  bandY = drawLines(pen, patientAddressLines, { x: bandLeftX, y: bandY, size: 9, leading: 12 });
  if (content.patient.mrn !== undefined) {
    drawText(pen, `MRN ${content.patient.mrn}`, {
      x: bandLeftX,
      y: bandY,
      size: 9,
      color: palette.muted,
    });
  }

  let identifierY = bandTop - bandPadding - 8;
  drawText(pen, "DOB", {
    x: bandRightX,
    y: identifierY,
    size: 7,
    bold: true,
    color: palette.muted,
  });
  identifierY -= 11;
  drawText(pen, content.patient.dateOfBirth, { x: bandRightX, y: identifierY, size: 10 });
  identifierY -= 15;
  if (identifier !== undefined) {
    drawText(pen, identifier.label.toUpperCase(), {
      x: bandRightX,
      y: identifierY,
      size: 7,
      bold: true,
      color: palette.muted,
    });
    identifierY -= 11;
    identifierY = drawLines(pen, identifierValueLines, {
      x: bandRightX,
      y: identifierY,
      size: 10,
      leading: 12,
    });
    drawLines(pen, identifierNoteLines, {
      x: bandRightX,
      y: identifierY + 1,
      size: 7.5,
      leading: 10,
      color: palette.muted,
    });
  }
  return bandTop - bandHeight - 22;
}

/**
 * The signature block: the enrolled image (when there is one) above the rule,
 * then the prescriber's name, licence line and signing time.
 */
async function drawSignatureBlock(
  document: PDFDocument,
  pen: Pen,
  content: PrescriptionPadContent,
  options: RenderPrescriptionPadOptions,
  lineY: number,
): Promise<void> {
  if (options.signatureImage !== undefined) {
    const image =
      options.signatureImage.mimeType === "image/png"
        ? await document.embedPng(options.signatureImage.bytes)
        : await document.embedJpg(options.signatureImage.bytes);
    const scale = Math.min(
      SIGNATURE_IMAGE_MAX_WIDTH / image.width,
      SIGNATURE_IMAGE_MAX_HEIGHT / image.height,
      1,
    );
    pen.page.drawImage(image, {
      x: MARGIN + 4,
      y: lineY + 4,
      width: image.width * scale,
      height: image.height * scale,
    });
  }
  pen.page.drawLine({
    start: { x: MARGIN, y: lineY },
    end: { x: MARGIN + 240, y: lineY },
    thickness: 0.75,
    color: pen.palette.ink,
  });
  drawText(pen, content.prescriber.name, {
    x: MARGIN,
    y: lineY - 14,
    size: 10,
    bold: true,
  });
  drawText(pen, content.prescriber.licenseLine, {
    x: MARGIN,
    y: lineY - 26,
    size: 9,
    color: pen.palette.muted,
  });
  drawText(pen, content.signedAt, {
    x: MARGIN,
    y: lineY - 38,
    size: 8,
    color: pen.palette.muted,
  });
}

/** Drug-content attribution above the footer, then the colour band that closes the page. */
function drawNoticeAndFooter(pen: Pen, noticeLines: readonly string[]): void {
  if (noticeLines.length > 0) {
    drawLines(pen, noticeLines, {
      x: MARGIN,
      y: FOOTER_HEIGHT + 8 + (noticeLines.length - 1) * 8,
      size: 6.5,
      leading: 8,
      color: pen.palette.muted,
    });
  }
  // A plain colour band closes the pad; it carries no text.
  pen.page.drawRectangle({
    x: 0,
    y: 0,
    width: PAGE_WIDTH,
    height: FOOTER_HEIGHT,
    color: pen.palette.blue,
  });
}

/** One medication of a several-medication prescription, wrapped to its column. */
interface ListedMedication {
  readonly medicationLines: readonly string[];
  readonly sigLines: readonly string[];
  readonly detailLines: readonly string[];
  readonly dispense: string;
  readonly refills: string;
  readonly height: number;
}

const LIST_INDENT = 22;
const LIST_WIDTH = CONTENT_WIDTH - LIST_INDENT;
const LIST_MEDICATION = { size: 12, leading: 15 } as const;
const LIST_SIG = { size: 10.5, leading: 13.5 } as const;
const LIST_DETAIL = { size: 8.5, leading: 11.5 } as const;
/** Baseline step of the dispense and refills row. */
const LIST_DISPENSE_STEP = 14;
/** Room between two medications, with a hairline rule in the middle. */
const LIST_SEPARATOR = 18;
/** Several medications: the rule never drops below this baseline. */
const LIST_SIGNATURE_LINE_MIN_Y = 140;
/** Several medications: the rule sits this far below the last medication. */
const LIST_SIGNATURE_GAP = 84;

function listedMedication(pen: Pen, medication: PrescriptionPadMedication): ListedMedication {
  const medicationLines = wrappedLines(
    pen.bold,
    medication.medication,
    LIST_MEDICATION.size,
    LIST_WIDTH,
  );
  const sigLines = wrappedLines(pen.regular, medication.sig, LIST_SIG.size, LIST_WIDTH);
  const detailLines =
    medication.details.length === 0
      ? []
      : wrappedLines(pen.regular, medication.details.join("  ·  "), LIST_DETAIL.size, LIST_WIDTH);
  return {
    medicationLines,
    sigLines,
    detailLines,
    dispense: medication.dispense,
    refills: medication.refills,
    height:
      medicationLines.length * LIST_MEDICATION.leading +
      1 +
      sigLines.length * LIST_SIG.leading +
      2 +
      LIST_DISPENSE_STEP +
      detailLines.length * LIST_DETAIL.leading,
  };
}

/** Draws one listed medication from its first baseline; returns the next free baseline. */
function drawListedMedication(
  pen: Pen,
  listed: ListedMedication,
  number: number,
  startY: number,
): number {
  const { palette } = pen;
  const x = MARGIN + LIST_INDENT;
  drawText(pen, `${number}.`, {
    x: MARGIN,
    y: startY,
    size: LIST_MEDICATION.size,
    bold: true,
    color: palette.blue,
  });
  let y = drawLines(pen, listed.medicationLines, {
    x,
    y: startY,
    size: LIST_MEDICATION.size,
    leading: LIST_MEDICATION.leading,
    bold: true,
    color: palette.blue,
  });
  y -= 1;
  y = drawLines(pen, listed.sigLines, {
    x,
    y,
    size: LIST_SIG.size,
    leading: LIST_SIG.leading,
  });
  y -= 2;
  const labelSize = 7.5;
  const refillsX = x + LIST_WIDTH / 2;
  drawText(pen, "DISPENSE", { x, y, size: labelSize, bold: true, color: palette.blue });
  drawText(pen, listed.dispense, {
    x: x + pen.bold.widthOfTextAtSize("DISPENSE", labelSize) + 6,
    y,
    size: 10,
  });
  drawText(pen, "REFILLS", { x: refillsX, y, size: labelSize, bold: true, color: palette.blue });
  drawText(pen, listed.refills, {
    x: refillsX + pen.bold.widthOfTextAtSize("REFILLS", labelSize) + 6,
    y,
    size: 10,
  });
  y -= LIST_DISPENSE_STEP;
  return drawLines(pen, listed.detailLines, {
    x,
    y,
    size: LIST_DETAIL.size,
    leading: LIST_DETAIL.leading,
    color: palette.muted,
  });
}

/** Groups the listed medications into pages, keeping room for the signature on the last. */
function paginateListedMedications(
  listed: readonly ListedMedication[],
  bodyTop: number,
  pageFloor: number,
  lastPageFloor: number,
): number[][] {
  const pages: number[][] = [[]];
  let used = 0;
  for (const [index, entry] of listed.entries()) {
    const current = pages.at(-1) as number[];
    const needed = entry.height + (current.length > 0 ? LIST_SEPARATOR : 0);
    if (current.length > 0 && bodyTop - used - needed < pageFloor) {
      pages.push([index]);
      used = entry.height;
    } else {
      current.push(index);
      used += needed;
    }
  }
  // The signature follows the last medication on its page; when it does not
  // fit there, that medication moves to a new last page with it.
  const lastPage = pages.at(-1) as number[];
  const lastPageHeight = lastPage.reduce(
    (sum, index, position) =>
      sum + (listed[index] as ListedMedication).height + (position > 0 ? LIST_SEPARATOR : 0),
    0,
  );
  if (lastPage.length > 1 && bodyTop - lastPageHeight < lastPageFloor) {
    pages.push([lastPage.pop() as number]);
  }
  return pages;
}

export async function renderPrescriptionPad(
  content: PrescriptionPadContent,
  options: RenderPrescriptionPadOptions,
): Promise<Uint8Array> {
  const medications = content.medications;
  const first = medications[0];
  if (first === undefined) throw new Error("A prescription lists at least one medication.");
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const document = await PDFDocument.create();
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const signedAt = new Date(options.signedAtIso);
  const stamp = Number.isNaN(signedAt.getTime()) ? new Date(0) : signedAt;
  document.setTitle(`Prescription — ${pdfSafe(content.patient.name)}`);
  document.setAuthor(pdfSafe(content.prescriber.name));
  document.setSubject(
    pdfSafe(
      medications.length === 1
        ? first.medication
        : `${medications.length} medications: ${medications.map((entry) => entry.medication).join("; ")}`,
    ),
  );
  document.setCreator("EMR-REV prescription pad v2");
  document.setProducer("EMR-REV prescription pad v2");
  document.setCreationDate(stamp);
  document.setModificationDate(stamp);

  const palette: Palette = {
    blue: rgb(0.086, 0.31, 0.78),
    ink: rgb(0.13, 0.16, 0.2),
    muted: rgb(0.42, 0.46, 0.52),
    band: rgb(0.93, 0.955, 0.99),
    rule: rgb(0.84, 0.87, 0.93),
  };
  const noticeLines = (content.sourceNotice ?? []).flatMap((notice) =>
    wrappedLines(regular, notice, 6.5, CONTENT_WIDTH),
  );
  const newPen = (): Pen => ({
    page: document.addPage([PAGE_WIDTH, PAGE_HEIGHT]),
    regular,
    bold,
    palette,
  });

  if (medications.length === 1) {
    const pen = newPen();
    await drawSingleMedicationPad(document, pen, content, first, options);
    drawNoticeAndFooter(pen, noticeLines);
    return document.save({ useObjectStreams: false });
  }

  // ------------------------------------------------- several medications
  // Every page repeats the letterhead and patient band, so a page printed or
  // faxed on its own still names the prescriber and the patient.
  const countLabel = `${medications.length} medications`;
  const right = PAGE_WIDTH - MARGIN;
  const drawListHeader = (pen: Pen): { readonly y: number; readonly pageMarkerY: number } => {
    const rowTop = drawLetterheadAndPatient(pen, content);
    const rxY = rowTop - 26;
    drawText(pen, "Rx", { x: MARGIN, y: rxY, size: 40, bold: true, color: palette.blue });
    drawText(pen, countLabel, {
      x: MARGIN + bold.widthOfTextAtSize("Rx", 40) + 12,
      y: rxY + 2,
      size: 10,
      color: palette.muted,
    });
    drawText(pen, "DATE", {
      x: right,
      y: rowTop - 2,
      size: 7,
      bold: true,
      color: palette.muted,
      align: "right",
    });
    drawText(pen, content.date, {
      x: right,
      y: rowTop - 15,
      size: 11,
      bold: true,
      color: palette.blue,
      align: "right",
    });
    return { y: rxY - 30, pageMarkerY: rowTop - 28 };
  };

  const firstPen = newPen();
  const firstHeader = drawListHeader(firstPen);
  const listed = medications.map((medication) => listedMedication(firstPen, medication));
  // Medications stay clear of the drug-content notice; on the last page they
  // also leave the signature its room.
  const pageFloor =
    noticeLines.length === 0 ? FOOTER_HEIGHT + 24 : FOOTER_HEIGHT + 22 + noticeLines.length * 8;
  const pages = paginateListedMedications(
    listed,
    firstHeader.y,
    pageFloor,
    Math.max(pageFloor, LIST_SIGNATURE_LINE_MIN_Y + LIST_SIGNATURE_GAP),
  );

  for (const [pageIndex, indexes] of pages.entries()) {
    const pen = pageIndex === 0 ? firstPen : newPen();
    const header = pageIndex === 0 ? firstHeader : drawListHeader(pen);
    drawText(pen, `Page ${pageIndex + 1} of ${pages.length}`, {
      x: right,
      y: header.pageMarkerY,
      size: 8,
      color: palette.muted,
      align: "right",
    });
    let y = header.y;
    for (const [position, index] of indexes.entries()) {
      if (position > 0) {
        y -= 4;
        drawRule(pen, y, palette.rule, 0.5);
        y -= LIST_SEPARATOR - 4;
      }
      y = drawListedMedication(pen, listed[index] as ListedMedication, index + 1, y);
    }
    if (pageIndex === pages.length - 1) {
      await drawSignatureBlock(
        document,
        pen,
        content,
        options,
        Math.max(LIST_SIGNATURE_LINE_MIN_Y, y - LIST_SIGNATURE_GAP),
      );
    }
    drawNoticeAndFooter(pen, noticeLines);
  }
  return document.save({ useObjectStreams: false });
}

/** The single-medication pad: date, Rx, the medication's lines, then the signature. */
async function drawSingleMedicationPad(
  document: PDFDocument,
  pen: Pen,
  content: PrescriptionPadContent,
  medication: PrescriptionPadMedication,
  options: RenderPrescriptionPadOptions,
): Promise<void> {
  const { palette, regular, bold } = pen;
  let y = drawLetterheadAndPatient(pen, content);

  // ------------------------------------------------------------------ date
  drawText(pen, "DATE", { x: MARGIN, y, size: 7, bold: true, color: palette.muted });
  y -= 12;
  drawText(pen, content.date, { x: MARGIN, y, size: 11, bold: true, color: palette.blue });
  y -= 44;

  // -------------------------------------------------------------------- Rx
  drawText(pen, "Rx", { x: MARGIN, y, size: 40, bold: true, color: palette.blue });
  y -= 34;
  y = drawLines(pen, wrappedLines(bold, medication.medication, 15, CONTENT_WIDTH), {
    x: MARGIN,
    y,
    size: 15,
    leading: 19,
    bold: true,
    color: palette.blue,
  });
  y -= 2;
  drawRule(pen, y + 6, palette.rule);
  y -= 16;
  y = drawLines(pen, wrappedLines(regular, medication.sig, 12, CONTENT_WIDTH), {
    x: MARGIN,
    y,
    size: 12,
    leading: 16,
  });
  y -= 4;
  drawRule(pen, y + 6, palette.rule);
  y -= 18;

  const columnX = [MARGIN, MARGIN + CONTENT_WIDTH / 2];
  drawText(pen, "DISPENSE", {
    x: columnX[0] as number,
    y,
    size: 7.5,
    bold: true,
    color: palette.blue,
  });
  drawText(pen, "REFILLS", {
    x: columnX[1] as number,
    y,
    size: 7.5,
    bold: true,
    color: palette.blue,
  });
  y -= 14;
  drawText(pen, medication.dispense, { x: columnX[0] as number, y, size: 12 });
  drawText(pen, medication.refills, { x: columnX[1] as number, y, size: 12 });
  y -= 22;
  for (const detail of medication.details) {
    y = drawLines(pen, wrappedLines(regular, detail, 9.5, CONTENT_WIDTH), {
      x: MARGIN,
      y,
      size: 9.5,
      leading: 13,
    });
  }

  // --------------------------------------------------------------- signature
  // Sits just under the prescription body, as on a printed pad, but never
  // below the floor that keeps it clear of the footer.
  await drawSignatureBlock(
    document,
    pen,
    content,
    options,
    Math.max(SIGNATURE_LINE_MIN_Y, y - SIGNATURE_BLOCK_GAP),
  );
}
